import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { getRecipeEmoji } from '../../utils/getRecipeEmoji';

/**
 * RecipeDetails - Details d'une recette
 *
 * V3.0: Mobile-First Refactoring
 * - Ne gere plus son propre Drawer (parent le fait)
 * - Header sticky avec bouton fermer
 * - Instructions parsing intelligent
 * - Grid ingredients 2 colonnes mobile
 * - Macros en cards visuelles
 */
const RecipeDetails = ({ recipe, targetMacros, open, onClose, isMobile }) => {
  if (!recipe) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Aucune recette disponible</Typography>
      </Box>
    );
  }

  // Parsing intelligent des instructions
  const parseInstructions = () => {
    // Priorite 1: instructionSteps (array structure)
    if (recipe.instructionSteps && recipe.instructionSteps.length > 0) {
      return recipe.instructionSteps
        .sort((a, b) => (a.step || 0) - (b.step || 0))
        .map((step) => step.description || step)
        .filter((s) => s);
    }

    // Priorite 2: instructions (string)
    const instructions = recipe.instructions;
    if (!instructions) return [];

    // Si c'est deja un array
    if (Array.isArray(instructions)) {
      return instructions.filter((i) => i && (typeof i === 'string' ? i.trim() : i));
    }

    // Essayer de parser comme JSON
    try {
      const parsed = JSON.parse(instructions);
      if (Array.isArray(parsed)) {
        return parsed.filter((i) => i && (typeof i === 'string' ? i.trim() : i));
      }
    } catch (e) {
      // Pas du JSON, continuer
    }

    // Split par lignes d'abord, puis par points si necessaire
    let steps = instructions
      .split(/\n/)
      .map((s) => s.trim())
      .filter((s) => s);

    // Si une seule ligne, split par points (mais garder les decimales)
    if (steps.length === 1) {
      steps = instructions
        .split(/\.(?=\s|$)/)
        .map((s) => s.trim())
        .filter((s) => s);
    }

    // Nettoyer les numeros de debut (1. 2. etc)
    return steps.map((step) => step.replace(/^\d+[\.\)]\s*/, '').trim()).filter((s) => s);
  };

  const instructionSteps = parseInstructions();

  // Formatage de la quantite avec unite
  const formatIngredient = (ingredient) => {
    const qty = Math.round(ingredient.quantity || 0);
    const unit = ingredient.unit || 'g';
    return `${qty} ${unit}`;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header sticky */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Handle pour le drawer bottom mobile */}
        {isMobile && (
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'grey.300',
              borderRadius: 2,
              mx: 'auto',
              mt: 1,
            }}
          />
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, pr: 1 }} noWrap>
            {recipe.name}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Contenu scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 4 }}>
        {/* Image/Emoji header */}
        <Box
          sx={{
            height: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            fontSize: '4rem',
          }}
        >
          {getRecipeEmoji(recipe.name)}
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Temps de preparation */}
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {recipe.preparationTime && (
              <Chip
                icon={<AccessTimeIcon />}
                label={`Prep: ${recipe.preparationTime} min`}
                size="small"
                variant="outlined"
              />
            )}
            {recipe.cookingTime && (
              <Chip
                icon={<LocalFireDepartmentIcon />}
                label={`Cuisson: ${recipe.cookingTime} min`}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Macros en cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
              mb: 3,
            }}
          >
            {[
              { label: 'Kcal', value: recipe.macros?.calories, color: 'primary.main' },
              { label: 'Prot', value: recipe.macros?.proteins, unit: 'g', color: 'success.main' },
              { label: 'Gluc', value: recipe.macros?.carbohydrates, unit: 'g', color: 'warning.main' },
              { label: 'Lip', value: recipe.macros?.fats, unit: 'g', color: 'error.main' },
            ].map((macro) => (
              <Box
                key={macro.label}
                sx={{
                  textAlign: 'center',
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, color: macro.color }}
                >
                  {Math.round(macro.value || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {macro.label}
                  {macro.unit && ` (${macro.unit})`}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Ingredients */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Ingredients ({recipe.ingredients?.length || 0})
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              mb: 3,
            }}
          >
            {recipe.ingredients?.map((ingredient, index) => (
              <Card
                key={index}
                sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '1.1rem',
                        bgcolor: 'grey.100',
                      }}
                    >
                      {getRecipeEmoji(ingredient.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatIngredient(ingredient)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ingredient.name}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Instructions */}
          {instructionSteps.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Instructions
              </Typography>
              <Box sx={{ mb: 3 }}>
                {instructionSteps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: index < instructionSteps.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Typography variant="body2" sx={{ pt: 0.25 }}>
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Note info */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'info.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main',
            }}
          >
            <Typography variant="caption" color="info.dark">
              Les quantites ont ete ajustees automatiquement selon vos besoins caloriques.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipeDetails;
